/**
 * Product analytics (PostHog) — funnel instrumentation.
 *
 * Tracks the visitor → signup → activation → paid-upgrade funnel so we can
 * see where real traffic drops off. Every event is tagged with whatever
 * first-touch UTM parameters are currently stored (see `use-utm.ts`) so
 * conversion can be sliced by acquisition channel.
 *
 * Privacy: we only ever identify by the Clerk user id (already collected
 * elsewhere in the app). We never send email, name, or other PII to PostHog.
 */

import posthog from 'posthog-js';
import { getStoredUtmParams } from '@/hooks/use-utm';

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST as string | undefined;

let initialized = false;

/** Initialize the PostHog client. No-ops (with a console warning) if unconfigured. */
export function initAnalytics(): void {
  if (!POSTHOG_KEY) {
    console.warn(
      '[analytics] VITE_POSTHOG_KEY is not set — product analytics disabled.',
    );
    return;
  }

  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST || 'https://us.i.posthog.com',
    // We fire our own funnel-specific pageview event (landing_page_viewed)
    // rather than tracking every route change.
    capture_pageview: false,
    // Don't create/merge identified profiles until we explicitly identify()
    // a signed-in user — keeps anonymous browsing lightweight.
    person_profiles: 'identified_only',
    on_request_error: (err) => console.error('[analytics] request error', err),
  });
  initialized = true;
}

/** Canonical funnel event names, kept in one place to avoid typos/drift. */
export const FUNNEL_EVENTS = {
  LANDING_PAGE_VIEWED: 'landing_page_viewed',
  SIGNUP_STARTED: 'signup_started',
  SIGNUP_COMPLETED: 'signup_completed',
  ANALYSIS_RUN: 'analysis_run',
  CHECKOUT_STARTED: 'checkout_started',
  WATCHLIST_ADDED: 'watchlist_added',
  WATCHLIST_REMOVED: 'watchlist_removed',
  PRICE_ALERT_CREATED: 'price_alert_created',
  PRICE_ALERT_TOGGLED: 'price_alert_toggled',
  PRICE_ALERT_DELETED: 'price_alert_deleted',
  CHAT_MESSAGE_SENT: 'chat_message_sent',
  REFERRAL_LINK_COPIED: 'referral_link_copied',
  REFERRAL_LINK_SHARED: 'referral_link_shared',
  UPGRADE_COMPLETED: 'upgrade_completed',
  NOTIFICATIONS_TOGGLED: 'notifications_toggled',
  MARKET_VIEWED: 'market_viewed',
  SUBSCRIPTION_CANCELLED: 'subscription_cancelled',
} as const;

/** Track a funnel event, automatically attaching stored UTM attribution. */
export function trackEvent(name: string, properties: Record<string, unknown> = {}): void {
  if (!initialized) {
    console.warn('[analytics] trackEvent called before init, dropping:', name);
    return;
  }
  const utm = getStoredUtmParams() ?? {};
  posthog.capture(name, { ...utm, ...properties });
}

/**
 * Identify the current user by Clerk user id only (no email/name) so
 * subsequent events are attributed to a real person instead of an
 * anonymous device id.
 */
export function identifyUser(userId: string): void {
  if (!initialized) return;
  const utm = getStoredUtmParams();
  posthog.identify(userId, utm && Object.keys(utm).length ? { acquisition_utm: utm } : undefined);
}
